import { useEffect, useState } from 'react';
import { api, isSignedOut, type MeView } from '../api.ts';
import { navigate, CONSOLE_PATH, SIGN_IN_PATH } from '../route.ts';
import { Accounts } from './Accounts.tsx';
import { AccountScreen } from './AccountScreen.tsx';
import { Packages } from './Packages.tsx';
import { PlatformBilling } from './PlatformBilling.tsx';
import { PlatformEstate } from './PlatformEstate.tsx';
import { PlatformSupport } from './PlatformSupport.tsx';
import { Scores } from './Scores.tsx';
import { Waitlist } from './Waitlist.tsx';
import { Audit } from '../admin/Audit.tsx';
import '../shell.css';
import '../platform.css';

/**
 * The platform area: DriftWatch's own view of its customers.
 *
 * **The word "client" means an account here.** Everywhere in `/app` a client is
 * a Microsoft 365 tenant somebody assesses; on these screens it is the firm
 * paying us, and their tenants are a property of them. The vocabulary is the
 * reason this is a separate document rather than a section of the console —
 * two meanings of the same word on one screen is how somebody suspends the
 * wrong thing.
 *
 * The guard here is a courtesy, not a boundary. Every route these screens call
 * declares a requirement no customer role can hold, so a customer who reaches
 * this URL gets a screen that says so and a set of 403s behind it. Nothing is
 * protected by the fact that a link was not rendered.
 */

type Auth =
  | { readonly phase: 'loading' }
  | { readonly phase: 'out' }
  | { readonly phase: 'in'; readonly me: MeView };

const TABS = [
  { id: 'accounts', label: 'Clients' },
  { id: 'packages', label: 'Packages' },
  // Beside Packages: a package caps an account, a price is what each of its
  // clients costs. Two different levers, and staff reach for both together.
  { id: 'billing', label: 'Prices' },
  { id: 'estate', label: 'Tenants' },
  // Staff only, like everything in this area — and the one section whose
  // content a customer must never see even as a screenshot (ADR-0081).
  { id: 'scores', label: 'Scores' },
  { id: 'waitlist', label: 'Waiting list' },
  { id: 'support', label: 'Support' },
  { id: 'audit', label: 'Audit log' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export const Platform = () => {
  const [auth, setAuth] = useState<Auth>({ phase: 'loading' });
  const [tab, setTab] = useState<TabId>('accounts');
  /** The account being looked at, or null for the list. */
  const [accountId, setAccountId] = useState<string | null>(null);

  useEffect(() => {
    void api
      .me()
      .then((me) => setAuth({ phase: 'in', me }))
      .catch(() => setAuth({ phase: 'out' }));
  }, []);

  if (auth.phase === 'loading') {
    return (
      <main className="pf-shell">
        <p className="note">Checking your session…</p>
      </main>
    );
  }

  if (auth.phase === 'out') {
    return (
      <main className="pf-shell">
        <div className="card">
          <div className="card-body">
            <h1>Sign in first</h1>
            <p className="lead">The platform area needs a signed-in session.</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate(SIGN_IN_PATH)}>
              Go to sign in
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!auth.me.platformOwner) {
    /*
     * Says what it is and nothing about what is behind it. A customer's owner
     * who lands here learns that this is not theirs; the existence and shape of
     * the platform area is not something their account entitles them to, and
     * the API's own 403 is worded the same way.
     */
    return (
      <main className="pf-shell">
        <div className="card">
          <div className="card-body">
            <h1>Not available</h1>
            <p className="lead">This area is not available to your account.</p>
            <button type="button" className="btn" onClick={() => navigate(CONSOLE_PATH)}>
              Back to the console
            </button>
          </div>
        </div>
      </main>
    );
  }

  const current = TABS.find((entry) => entry.id === tab) ?? TABS[0];

  /*
   * The console's frame, not a copy of it: the same sidebar, top bar and
   * classes from shell.css, so the two areas read as one product. What differs
   * is the content of the sidebar — this area's sections, and a way back to
   * the console where the console keeps its way here.
   */
  return (
    <div className="shell">
      <a className="skip-link" href="#content">
        Skip to content
      </a>

      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-brand-name">DriftWatch</span>
        </div>

        <nav className="sidebar-nav" aria-label="Platform sections">
          <div className="nav-group" role="group" aria-labelledby="nav-platform">
            <h2 className="label nav-heading" id="nav-platform">
              Platform
            </h2>
            <ul className="nav-list">
              {TABS.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="nav-item"
                    aria-current={tab === entry.id ? 'page' : undefined}
                    onClick={() => {
                      setTab(entry.id);
                      setAccountId(null);
                    }}
                  >
                    {entry.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button type="button" className="link sidebar-platform" onClick={() => navigate(CONSOLE_PATH)}>
            Back to the console
          </button>
        </div>
      </aside>

      <div className="shell-body">
        <header className="topbar">
          <div className="topbar-inner">
            <div>
              <p className="crumb">
                Platform
                <span className="crumb-sep" aria-hidden="true">
                  /
                </span>
                {current.label}
              </p>
              <h1>{current.label}</h1>
            </div>
          </div>
        </header>

        <main id="content">
          <p className="pf-sub pf-vocabulary">
            DriftWatch’s own customers. A <strong>client</strong> here is an account, not a tenant —
            their tenants are listed inside them.
          </p>

          {tab === 'accounts' &&
            (accountId === null ? (
              <Accounts onOpen={setAccountId} />
            ) : (
              <AccountScreen accountId={accountId} onBack={() => setAccountId(null)} />
            ))}
          {tab === 'packages' && <Packages />}
          {tab === 'billing' && <PlatformBilling />}
          {tab === 'estate' && <PlatformEstate />}
          {tab === 'scores' && <Scores />}
          {tab === 'waitlist' && <Waitlist />}
          {tab === 'support' && <PlatformSupport />}
          {tab === 'audit' && (
            <Audit
              read={api.platformAuditLog}
              lead="Everything done anywhere in this deployment — by every client’s operators, by the scheduler and the break-glass token, and by DriftWatch staff here. Newest first."
            />
          )}
        </main>
      </div>
    </div>
  );
};

/** Shared by the screens below: a failure reads as a sentence, never as JSON. */
export const message = (cause: unknown): string =>
  isSignedOut(cause)
    ? 'Your session has ended. Sign in again.'
    : cause instanceof Error
      ? cause.message
      : 'Something went wrong.';

/** `null` is unlimited everywhere in a package, and must never render as blank. */
export const limit = (value: number | null): string =>
  value === null ? 'Unlimited' : String(value);

/**
 * Usage against a limit, and whether it is over.
 *
 * Over is a real state rather than an impossible one: a package changed
 * downwards leaves an account above a limit it never crossed, and that account
 * has stopped being assessed. It needs to be visible at a glance on the list
 * rather than discovered by arithmetic.
 */
export const overLimit = (used: number, allowed: number | null): boolean =>
  allowed !== null && used > allowed;
